// Paper Lantern 9 (world.json bikerBars "paper9"): the Kaminari-gumi leadership's drinking spot, a 4x6-tile plaza yard
// in the flats block north-east of the Okubo tower, open to the east sidewalk (x242) and the south sidewalk.
// Ground (wood floor, walls, counter, boss tatami, the lantern sign) is painted flat inside the yard rect; stools, tables,
// lamps and the overhead lantern canopy are y-sorted props. Presentation only: no collision, nothing stands in the
// east column (x241), so the entrance stays clear. Palette comes from hd-kit (theme.ts); 提灯 is a custom bitmap since
// the glyph table is katakana/Latin only. Called from hd-scene.ts (paintPaper9 on the ground, paper9Props with props).
import { WORLD, type Rect } from '../../common/src/data';
import { TILE, inTileRect } from '../../common/src/map';
import { canvas } from './canvas';
import { type G, K, HT, px, disc, dk, lt, alpha, dither, speckle, shadow } from './hd-kit';
import type { LightFn } from './hd-registry';
import type { HdLight } from './hd-scene';

type Prop = (cv: HTMLCanvasElement, wx: number, wy: number, ax: number, ay: number) => void;
const bar = () => WORLD.bikerBars?.find(b => b.id === 'paper9');

/** 提 (hand + 是) and 灯 (fire + 丁), 13 x 13 cells; keep the radicals separated. */
const TEI = [
  '..#...######.', '..#...#....#.', '#####.######.', '..#...#....#.', '..#...######.',
  '..#..........', '..###.#######', '###......#...', '..#...#..#...', '..#...#..####',
  '..#...#..#...', '..#..#.#.#...', '.##.#...#####',
];
const CHOU = [
  '..#..........', '..#...#######', '..#..#...#...', '#.#.#....#...', '#.##.....#...',
  '#.#......#...', '..#......#...', '..#......#...', '.#.#.....#...', '.#..#....#...',
  '.#.......#...', '#........#...', '#......###...',
];
// A closed bowl and descending tail; the six-row shared glyph loses the bowl at this sign size.
const NINE = ['.###.', '#...#', '#...#', '#...#', '.####', '....#', '...#.', '..#..', '.#...'];
const CANOPY_ROWS = [70, 150];
function bitmap(g: G, rows: string[], x: number, y: number, s: number, c: string) {
  rows.forEach((r, j) => { for (let i = 0; i < r.length; i++) if (r[i] === '#') px(g, x + i * s, y + j * s, s, s, c); });
}
function glowBlob(g: G, x: number, y: number, rx: number, ry: number, col: string, a: number) {
  g.save(); g.globalCompositeOperation = 'lighter'; g.translate(x, y); g.scale(1, ry / rx);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, rx); grd.addColorStop(0, alpha(col, a)); grd.addColorStop(1, alpha(col, 0));
  g.fillStyle = grd; g.fillRect(-rx, -rx, rx * 2, rx * 2); g.restore();
}
/** a small round paper lantern hanging from y (top cap) at cx */
function lantern(g: G, cx: number, y: number, c: string) {
  disc(g, cx, y + 7, 6, 7, K.ink); disc(g, cx, y + 7, 5, 6, c);
  px(g, cx - 3, y + 3, 2, 3, lt(c, 0.4)); px(g, cx - 5, y + 6, 11, 1, dk(c, 0.45)); px(g, cx - 4, y + 9, 9, 1, dk(c, 0.45));
  px(g, cx - 3, y, 7, 2, K.ink); px(g, cx - 3, y + 13, 7, 2, K.ink); px(g, cx, y + 15, 1, 4, K.warmHi);
}

// ================================================================== ground (hd px, scene-relative)
export function paintPaper9(g: G, R: Rect, L: LightFn) {
  const b = bar(); if (!b || !inTileRect(b.x, b.y, R)) return;
  const ox = (b.x - R.x) * HT, oy = (b.y - R.y) * HT, W = b.w * HT, H = b.h * HT;
  g.save(); g.beginPath(); g.rect(ox, oy, W, H); g.clip();

  // ---- wooden floor: planks with offset seams, warm lacquer sheen toward the counter
  for (let j = 0; j < H; j += 6) {
    const pl = j % 12 ? K.wood : dk(K.wood, 0.12);
    px(g, ox, oy + j, W, 5, pl); px(g, ox, oy + j + 5, W, 1, K.woodLo);
    for (let i = ((j / 6) % 3) * 17 + 9; i < W; i += 51) px(g, ox + i, oy + j, 1, 5, K.woodLo);
  }
  speckle(g, ox, oy, W, H, alpha(K.night, 0.3), 0.05, 83, 2);
  dither(g, ox, oy + 40, W, 18, alpha(K.night, 0.25));
  dither(g, ox, oy + H - 18, W, 18, alpha(K.night, 0.22), 1);

  // ---- north wall (v0-40): dark panelling, lit shoji windows, ceiling beam
  px(g, ox, oy, W, 40, K.ink); px(g, ox, oy + 6, W, 34, K.wall);
  for (let i = 0; i < W; i += 8) px(g, ox + i, oy + 6, 4, 34, i % 16 ? K.wallHi : K.wall);
  px(g, ox, oy, W, 6, K.woodLo); px(g, ox, oy + 5, W, 1, K.ink); px(g, ox, oy + 1, W, 1, K.woodHi);
  px(g, ox, oy + 34, W, 6, K.woodLo); px(g, ox, oy + 34, W, 1, K.woodHi); px(g, ox, oy + 39, W, 1, K.ink);
  px(g, ox, oy + 40, W, 3, alpha(K.night, 0.5)); px(g, ox, oy + 43, W, 4, alpha(K.night, 0.22));
  // shoji screen behind the boss table, golden paper with a pink crane stroke
  const sx = ox + 14, sw = 44;
  px(g, sx - 1, oy + 8, sw + 2, 27, K.ink);
  for (let i = 0; i < 4; i++) {
    const x = sx + i * 11;
    px(g, x, oy + 9, 10, 25, i % 2 ? dk(K.warm, 0.25) : dk(K.warm, 0.4));
    px(g, x, oy + 9, 1, 25, K.woodLo); px(g, x, oy + 21, 10, 1, alpha(K.woodLo, 0.7));
  }
  px(g, sx + 6, oy + 24, 14, 2, K.pink); px(g, sx + 18, oy + 20, 8, 2, K.pink); px(g, sx + 24, oy + 16, 3, 5, K.pink); px(g, sx + 26, oy + 14, 4, 2, K.white);
  px(g, sx + 28, oy + 28, 10, 2, alpha(K.pink, 0.6));
  L(sx, oy + 9, sw, 25, K.amber, 0.2, true);
  // west wall panelling (the back-bar shelf sits on it)
  px(g, ox, oy + 40, 12, H - 40, K.ink); px(g, ox, oy + 40, 11, H - 40, K.wallLo);
  for (let j = 42; j < H; j += 8) px(g, ox, oy + j, 11, 1, K.ink);
  px(g, ox + 11, oy + 40, 1, H - 40, K.ink); px(g, ox + 12, oy + 40, 3, H - 40, alpha(K.night, 0.4));

  // ---- boss table: raised tatami in the NW corner with a low table, zabuton and a sake set
  const tx = ox + 16, ty = oy + 46, tw = 50, th = 46;
  px(g, tx - 2, ty - 2, tw + 4, th + 4, K.ink); px(g, tx, ty, tw, th, K.plaster);
  for (let j = 0; j < th; j += 12) px(g, tx, ty + j + 11, tw, 1, K.plasterLo);
  px(g, tx + tw / 2, ty, 1, th, K.plasterLo); speckle(g, tx, ty, tw, th, K.plasterLo, 0.1, 5);
  px(g, tx, ty, tw, 2, K.white); px(g, tx, ty + th - 3, tw, 3, K.plasterLo); // raised edge
  px(g, tx - 2, ty + th, tw + 4, 3, alpha(K.night, 0.5));
  px(g, tx + 9, ty + 12, 32, 20, K.ink); px(g, tx + 10, ty + 13, 30, 18, K.woodLo); px(g, tx + 10, ty + 13, 30, 2, K.woodHi); // table
  for (const [cx, cy] of [[tx + 6, ty + 22], [tx + 44, ty + 22]] as const) { px(g, cx - 4, cy - 7, 9, 14, K.ink); px(g, cx - 3, cy - 6, 7, 12, K.red); px(g, cx - 3, cy - 6, 7, 2, lt(K.red, 0.3)); } // zabuton
  px(g, tx + 14, ty + 16, 8, 8, K.ink); px(g, tx + 15, ty + 17, 6, 6, K.glassLo); px(g, tx + 15, ty + 17, 2, 6, K.glassHi); // bottle
  px(g, tx + 26, ty + 17, 5, 5, K.ink); px(g, tx + 27, ty + 18, 3, 3, K.white); px(g, tx + 32, ty + 23, 5, 5, K.ink); px(g, tx + 33, ty + 24, 3, 3, K.white);
  glowBlob(g, tx + 25, ty + 22, 24, 18, K.warm, 0.1);
  L(tx - 4, ty - 4, tw + 8, th + 8, K.amber, 0.16);

  // ---- counter along the west wall: lacquered top in plan, back-bar bottles on the wall shelf
  const cx0 = ox + 16, cy0 = oy + 104, cw = 24, ch = 76;
  px(g, cx0 - 2, cy0 - 2, cw + 4, ch + 4, K.ink); px(g, cx0, cy0, cw, ch, K.woodHi);
  for (let j = 0; j < ch; j += 9) px(g, cx0, cy0 + j + 8, cw, 1, K.woodLo);
  px(g, cx0, cy0, 3, ch, lt(K.woodHi, 0.3)); px(g, cx0 + cw - 3, cy0, 3, ch, K.woodLo); // sheen on the wall side, shade on the stool side
  px(g, cx0 + cw, cy0 - 1, 3, ch + 5, alpha(K.night, 0.5)); // shadow
  const bc = [K.glassHi, K.warm, K.pink, K.leafHi, K.white, K.warmHi];
  for (let i = 0; i < 8; i++) { // bottles and a cup row on the counter, tops down the wall
    const y = cy0 + 5 + i * 9;
    if (i % 3 !== 2) { px(g, cx0 + 8, y, 5, 5, K.ink); px(g, cx0 + 9, y + 1, 3, 3, bc[i % 6]); } else { px(g, cx0 + 15, y + 1, 4, 4, K.ink); px(g, cx0 + 16, y + 2, 2, 2, K.white); }
  }
  for (let i = 0; i < 7; i++) { // wall shelf with bottles
    const y = oy + 108 + i * 10; px(g, ox + 1, y, 10, 3, K.woodHi); px(g, ox + 1, y + 3, 10, 1, K.ink);
    px(g, ox + 2, y - 5, 3, 5, bc[i % 6]); px(g, ox + 6, y - 4, 3, 4, bc[(i + 2) % 6]); px(g, ox + 2, y - 5, 1, 5, lt(bc[i % 6], 0.4));
  }
  L(ox + 1, oy + 100, 10, 80, K.amber, 0.18);
  L(cx0 - 4, cy0, cw + 12, ch, K.amber, 0.12);

  // ---- 提灯9 sign: a wide paper lantern hung on the north wall by the entrance, pink-lit
  const lx = ox + 90, ly = oy + 20, rx = 36, ry = 17;
  px(g, lx - 1, oy, 3, ly - ry + 1, K.ink); px(g, lx - 8, ly - ry - 3, 17, 5, K.ink); px(g, lx - 7, ly - ry - 2, 15, 3, K.woodHi); // hook and cap
  disc(g, lx, ly, rx + 1, ry + 1, K.ink); disc(g, lx, ly, rx, ry, dk(K.pink, 0.2));
  for (let i = -3; i <= 3; i++) { // ribs follow the curve
    const hh = Math.round(ry * Math.sqrt(1 - (i * i) / 12.5)); px(g, lx + i * 11 - 1, ly - hh, 1, hh * 2 + 1, dk(K.pink, 0.55));
  }
  disc(g, lx - 14, ly - 8, 8, 3, lt(K.pink, 0.2));
  px(g, lx - 9, oy + 36, 18, 2, K.ink); px(g, lx - 6, ly + ry, 12, 3, K.ink); px(g, lx - 1, ly + ry + 3, 3, 8, K.warmHi); // lower cap and tassel
  const gx = lx - 33, gy = ly - 13;
  // A dark inset keeps the lettering legible against pink and stops the lantern ribs breaking its strokes.
  px(g, gx - 2, gy - 1, 70, 28, K.ink);
  bitmap(g, TEI, gx, gy, 2, K.white); bitmap(g, CHOU, gx + 28, gy, 2, K.white);
  bitmap(g, NINE, gx + 56, gy + 4, 2, K.white);
  L(lx - rx, ly - ry, rx * 2, ry * 2, K.pink, 0.32, true, true);
  L(lx - 50, ly, 100, 70, K.pink, 0.08);
  glowBlob(g, lx, ly + 8, 54, 30, K.pink, 0.07);
  g.restore();
}

// ================================================================== props (y-sorted, no collision)
interface Sprite { cv: HTMLCanvasElement; ax: number; ay: number }

/** a round stool with a red cushion */
function stool(): Sprite {
  const [cv, g] = canvas(20, 28), ax = 10, ay = 25;
  shadow(g, 3, 20, 16, 4, 0.4);
  px(g, 8, 13, 4, 10, K.ink); px(g, 9, 13, 2, 10, K.metal); px(g, 5, 21, 10, 3, K.ink);
  disc(g, 10, 11, 8, 5, K.ink); disc(g, 10, 10, 7, 4, K.red); disc(g, 9, 9, 4, 2, lt(K.red, 0.3));
  return { cv, ax, ay };
}
/** a small barrel table (sake barrel top) with cups and a candle */
function table(): Sprite {
  const [cv, g] = canvas(40, 42), ax = 20, ay = 38;
  shadow(g, 5, 33, 32, 5, 0.4);
  px(g, 8, 18, 24, 18, K.ink); px(g, 9, 18, 22, 17, K.woodHi); px(g, 9, 18, 3, 17, lt(K.woodHi, 0.25)); px(g, 28, 18, 3, 17, K.woodLo);
  for (const y of [22, 30]) { px(g, 8, y, 24, 2, K.ink); px(g, 9, y, 22, 1, K.metalLo); }
  disc(g, 20, 17, 13, 6, K.ink); disc(g, 20, 17, 12, 5, K.wood); disc(g, 19, 16, 8, 3, K.woodHi);
  px(g, 12, 13, 5, 5, K.ink); px(g, 13, 14, 3, 3, K.white); px(g, 24, 15, 5, 5, K.ink); px(g, 25, 16, 3, 3, K.white);
  px(g, 19, 8, 3, 8, K.ink); px(g, 20, 9, 1, 6, K.white); px(g, 20, 5, 1, 3, K.warmHi); glowBlob(g, 20, 8, 12, 9, K.warm, 0.25);
  return { cv, ax, ay };
}
/** a standing andon lamp: wooden frame, glowing paper panels */
function andon(): Sprite {
  const [cv, g] = canvas(24, 52), ax = 12, ay = 49;
  shadow(g, 3, 44, 20, 4, 0.4);
  px(g, 4, 42, 16, 5, K.ink); px(g, 5, 43, 14, 3, K.woodLo);
  px(g, 5, 8, 14, 35, K.ink); px(g, 7, 10, 10, 31, K.warmHi); px(g, 7, 10, 10, 5, K.white);
  px(g, 11, 10, 2, 31, K.woodLo); px(g, 7, 25, 10, 1, K.woodLo); px(g, 3, 5, 18, 5, K.ink); px(g, 4, 6, 16, 2, K.woodHi);
  glowBlob(g, 12, 26, 18, 24, K.warm, 0.2);
  return { cv, ax, ay };
}
/** a stack of sake barrels (kazaridaru) with red straps */
function barrels(): Sprite {
  const [cv, g] = canvas(40, 46), ax = 20, ay = 43;
  shadow(g, 3, 38, 36, 5, 0.4);
  const bar1 = (x: number, y: number) => {
    px(g, x, y, 16, 14, K.ink); px(g, x + 1, y + 1, 14, 12, K.woodHi); px(g, x + 1, y + 1, 3, 12, lt(K.woodHi, 0.25));
    px(g, x, y + 3, 16, 2, K.ink); px(g, x, y + 9, 16, 2, K.ink); px(g, x + 1, y + 4, 14, 1, K.metalLo); px(g, x + 1, y + 10, 14, 1, K.metalLo);
    px(g, x + 6, y + 5, 4, 4, K.red);
  };
  bar1(4, 26); bar1(20, 26); bar1(12, 12);
  px(g, 13, 14, 14, 2, lt(K.woodHi, 0.3));
  return { cv, ax, ay };
}
/** the overhead lantern canopy: ropes of red paper lanterns across the whole yard, drawn above the people under it */
function canopy(W: number, H: number): Sprite {
  const [cv, g] = canvas(W, H), ax = 0, ay = H + 28;
  CANOPY_ROWS.forEach((y, r) => {
    const step = 24, x0 = 14 + (r % 2) * 12;
    for (let x = 8; x < W - 2; x++) px(g, x, y + Math.round(2 * Math.sin(Math.PI * (x % 32) / 32)), 1, 1, alpha(K.ink, 0.85));
    for (let x = x0; x < W - 8; x += step) {
      const c = ((x / step + r) | 0) % 3 === 2 ? K.warm : ((x / step + r) | 0) % 2 ? K.red : K.pink;
      lantern(g, x, y + 2, c);
      glowBlob(g, x, y + 10, 12, 11, c === K.warm ? K.warm : K.red, 0.1);
    }
  });
  return { cv, ax, ay };
}

export function paper9Props(R: Rect, prop: Prop, lights: HdLight[]) {
  const b = bar(); if (!b || !inTileRect(b.x, b.y, R)) return;
  const bx = b.x * TILE, by = b.y * TILE, W = b.w * HT, H = b.h * HT;
  const put = (s: Sprite, u: number, v: number) => prop(s.cv, bx + u / 2, by + v / 2, s.ax, s.ay);
  const pool = (u: number, v: number, w: number, h: number, color: string, a: number) =>
    lights.push({ x: bx + u / 2 - w / 2, y: by + v / 2 - h / 2, w, h, color, alpha: a, pool: true });
  const st = stool(), tb = table();
  for (const v of [124, 150, 176]) put(st, 50, v);
  put(tb, 78, 132); put(st, 62, 142); put(st, 94 - 6, 142);
  put(tb, 78, 176);
  put(andon(), 76, 96); put(barrels(), 78, 62);
  put(canopy(W, H), 0, H + 28);
  // red light pooled under each lantern row, amber at the counter and the boss table
  for (const v of CANOPY_ROWS) pool(64, v + 14, 112, 26, K.red, 0.1);
  pool(30, 140, 36, 70, K.amber, 0.14); pool(40, 70, 56, 46, K.amber, 0.14); pool(76, 96, 24, 24, K.amber, 0.14);
}
